import React, { FC, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Flex,
  Heading,
  LoadingIndicator,
  PrimaryButton,
  SimpleCarousel,
  Text,
} from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { trackGAEvent, useTrackGAEventOnce } from '@moonpig/web-core-analytics'
import { useQuery } from '@moonpig/web-core-graphql'
import { useRouter } from '@moonpig/web-core-routing'
import { useStoreId } from '@moonpig/web-core-stores'
import { logger } from '@moonpig/web-core-monitoring'
import { useInView } from 'react-intersection-observer'
import { GetBasketGQL } from '../../queries/addDigitalGiftToBasket/getCardOptions'
import { GetBasketQuery } from '../../queries/addDigitalGiftToBasket/__generated__/getCardOptions'
import {
  GetBasket_me_basket_lineItems as LineItem,
  GetBasket_me_basket_lineItems_design as Design,
} from '../../utils/addDigitalGiftToBasket/types'
import { useProductStore } from '../../contexts/product'
import { useBasket } from '../../contexts/basket'
import { useAddDigitalGiftToExistingCard } from '../../utils/addDigitalGiftToBasket'
import { isDigitalGiftCard } from '../../utils/isDigitalGift/isDigitalGift'
import { useProductsLocaleText } from '../../locale'
import { CardThumbnail, CarouselItemBox, SelectNewTile } from './components'
import { ModuleContext, ModuleSelectCard } from '../types'

const SELECT_NEW_FRONT_THRESHOLD = 2

const StyledCarousel = styled(SimpleCarousel)`
  padding: 0;
`

type SelectableCard = Omit<LineItem, 'design'> & {
  design: Design & { coverImageUrl: string }
}

const getCoverImageUrl = (design: Design | null): string | null => {
  if (!design || design.images.__typename !== 'SceneImages') return null
  return design.images.images[0]?.variants.small.url ?? null
}

type Props = {
  module: ModuleSelectCard
  context: ModuleContext
}

export const SelectCardModule: FC<Props> = () => {
  const localiseText = useProductsLocaleText()
  const [{ product, variant, isVariantSelected }] = useProductStore()
  const [_, { setError, setSelectedAmountWarning }] = useBasket()
  const { addDigitalGiftToExistingCard, loading } =
    useAddDigitalGiftToExistingCard()
  const storeId = useStoreId()
  const router = useRouter()

  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)

  const { trackGAEventOnce } = useTrackGAEventOnce()
  const [moduleRef, inView] = useInView({ triggerOnce: true })

  useEffect(() => {
    if (inView) {
      trackGAEventOnce({
        event: 'impression',
        event_data: {
          action: 'view card selector',
          category: 'product details',
          label: 'select card module | what card should we add it to',
          non_interaction: true,
        },
      })
    }
  }, [inView, trackGAEventOnce])

  const shouldFilterECards = isDigitalGiftCard(product.category.department)

  const { data: basketData, loading: basketLoading } =
    useQuery<GetBasketQuery>(GetBasketGQL)

  const cards = useMemo<SelectableCard[]>(() => {
    const lineItems = basketData?.me.basket?.lineItems ?? []
    return lineItems
      .filter((item): item is LineItem & { design: Design } => {
        if (item.design === null) return false
        if (item.productType !== 'greetingcard') return false
        if (shouldFilterECards && item.size === 'ECard') return false
        return true
      })
      .map(item => {
        const coverImageUrl = getCoverImageUrl(item.design)
        return coverImageUrl
          ? { ...item, design: { ...item.design, coverImageUrl } }
          : null
      })
      .filter((item): item is SelectableCard => item !== null)
  }, [basketData, shouldFilterECards])

  const handleChooseNewCard = () => {
    if (isVariantSelected === false) {
      setSelectedAmountWarning(true)
      return
    }

    trackGAEvent({
      event: 'select_content',
      event_data: {
        action: 'select new card',
        category: 'product details',
        label: 'select card module | select new',
      },
    })

    router.push({
      name: 'add-a-card',
      params: {
        region: storeId,
        version: 1,
        digitalGift: `${product.id}:${variant.sku}`,
      },
    })
  }

  const basketId = basketData?.me.basket?.id

  const hasCards = cards.length > 0
  const selectedCard =
    cards.find(card => card.id === selectedCardId) ?? cards[0] ?? null

  const handleContinue = async () => {
    /* istanbul ignore next */
    if (!basketId) return

    if (isVariantSelected === false) {
      setSelectedAmountWarning(true)
      return
    }

    try {
      await addDigitalGiftToExistingCard({
        basketId,
        existingCard: selectedCard,
        variant,
        department: product.category.department,
      })
    } catch (error) {
      logger.fixToday(
        'Failure adding digital gift to existing card from PDP',
        error,
      )
      setError(true)
    }
  }

  const cardItems = cards.map(card => (
    <CarouselItemBox key={card.id}>
      <CardThumbnail
        id={card.id}
        name="select-card"
        title={card.title}
        imageUrl={card.design.coverImageUrl}
        isSelected={card.id === selectedCard?.id}
        onSelect={setSelectedCardId}
        ariaLabel={localiseText('select_card.card_aria_label', card.title)}
      />
    </CarouselItemBox>
  ))

  const selectNewItem = (
    <CarouselItemBox key="select-new">
      <SelectNewTile
        label={localiseText('select_card.select_new')}
        onClick={handleChooseNewCard}
      />
    </CarouselItemBox>
  )

  const carouselItems =
    cardItems.length > SELECT_NEW_FRONT_THRESHOLD
      ? [selectNewItem, ...cardItems]
      : [...cardItems, selectNewItem]

  return (
    <Box ref={moduleRef} mb={6} data-testid="module-select-card">
      <Flex flexDirection="column" width={1}>
        {basketLoading && (
          <Flex
            justifyContent="center"
            alignItems="center"
            minHeight="356px"
            minWidth={{ xs: '100%', md: '450px' }}
            mb={6}
            data-testid="mp-select-card-loading"
          >
            <LoadingIndicator label={localiseText('select_card.loading')} />
          </Flex>
        )}
        {!basketLoading && hasCards && (
          <>
            <Heading
              level="h2"
              mb={2}
              mt={0}
              typography={{
                xs: 'typeMobileDisplay04',
                md: 'typeDesktopDisplay05',
              }}
            >
              {localiseText('select_card.title')}
            </Heading>
            <Text mb={6} typography="typeBodyText">
              {localiseText('select_card.subtitle')}
            </Text>
            <Box mb={6} width={1}>
              <StyledCarousel
                gap={4}
                aria-label={localiseText('select_card.title')}
              >
                {carouselItems}
              </StyledCarousel>
            </Box>
            <PrimaryButton width={1} loading={loading} onClick={handleContinue}>
              {localiseText('select_card.cta_primary')}
            </PrimaryButton>
          </>
        )}

        {!basketLoading && !hasCards && (
          <PrimaryButton width={1} onClick={handleChooseNewCard}>
            {localiseText('select_card.cta_no_cards')}
          </PrimaryButton>
        )}
      </Flex>
    </Box>
  )
}
